import { z } from 'zod';
import { LEVELS, ROLES, CATEGORIES, define, DEFINITION_KINDS } from '../types.js';

/**
 * Fraud detection and audit configuration.
 */
export const schema = z
  .object({
    enabled: z
      .boolean()
      .describe(
        'Turn on fraud audit monitoring to detect potential fraud cases. This will flag any trips that are outside the configured distance and time tolerances.'
      )
      .optional(),
    time_tolerance: z
      .number()
      .int()
      .min(0)
      .max(120)
      .describe('Maximum allowed time deviation (in minutes) before flagging as potentially fraudulent behavior.')
      .optional(),
    distance_tolerance: z
      .number()
      .int()
      .min(0)
      .max(50)
      .describe('Maximum allowed distance deviation (in miles) before flagging as potentially fraudulent behavior.')
      .optional(),
  })
  .strict()
  .describe(
    'Fraud audit settings help detect potential fraud cases. This will flag any trips that are outside the configured distance and time tolerances.'
  );

export type FraudAudit = z.infer<typeof schema>;

export const definition = define({
  key: 'fraud_audit',
  schema,
  label: 'Fraud Audit',
  description:
    'Fraud audit settings help detect potential fraud cases. This will flag any trips that are outside the configured distance and time tolerances.',
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.FRAUD_AUDIT.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.REGION, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: { enabled: false, time_tolerance: 15, distance_tolerance: 5 },
});
